import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    ArrowRight,
    BarChart3,
    CheckCircle2,
    Eye,
    FileCheck,
    Lock,
    ShieldCheck,
    Trash2,
    Users,
    Edit3,
} from "lucide-react";
import { Link } from "react-router-dom";

import { THEME } from "./dashboard.constants";
import { claimRequest, getPageEligibility, removalRequest } from "../api/api";
import { toast } from "sonner";
import Modal from "../components/template/Modal";
import ContextualHelpButton from "../components/common/ContextualHelpButton";

export interface IData {
    dealerId: number,

    orderCount: number,

    isEligible: boolean,

    canClaimPage: boolean,

    canRemovePage: boolean,

    claimStatus: "Pending" | "Rejected" | "Live" | "Removal Pending" | "Removal Rejected" | "Removal Approved" | null,

    stats: {
        claimed: number,
        claimedRequests: number,
        pageViews: number,
        removalRequests: number
    }
}

const ClaimPage: React.FC = () => {
    const [data, setData] = useState<IData>()
    const [showClaimModal, setShowClaimModal] = useState(false)
    const [showRemoveModal, setShowRemoveModal] = useState(false)
    const [loading, setLoading] = useState(false)

    const getDealerPageEligiblity = async () => {
        try {
            const { data } = await getPageEligibility()
            console.log(data)
            setData(data.data)
        } catch (error: any) {
            toast.error(error?.response?.data.message || "failed to fetch eligiblity")
        }
    }

    const handleClaimPage = async () => {
        setLoading(true)
        try {
            const { data: resData } = await claimRequest()
            toast.success(resData?.message || "Claim request submitted successfully")
            setData((prev) => prev ? { ...prev, claimStatus: "Pending", canClaimPage: false } : prev)
            setShowClaimModal(false)
            await getDealerPageEligiblity()
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Failed to send claim request")
        } finally {
            setLoading(false)
            setShowClaimModal(false)
        }
    }

    const handleRemovePage = async () => {
        setLoading(true)
        try {
            const { data: resData } = await removalRequest()
            toast.success(resData?.message || "Removal request submitted successfully")
            setData((prev) => prev ? { ...prev, claimStatus: "Removal Pending", canRemovePage: false } : prev)
            setShowRemoveModal(false)
            await getDealerPageEligiblity()
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Failed to send removal request")
        } finally {
            setLoading(false)
            setShowRemoveModal(false)
        }
    }

    useEffect(() => {
        getDealerPageEligiblity()
    }, [])

    const statCards = [
        { label: "Total Claimed", value: data?.stats.claimed, icon: FileCheck },
        { label: "Removal Requests", value: data?.stats.removalRequests, icon: Trash2 },
        { label: "Page Views", value: data?.stats.pageViews, icon: Eye },
        { label: "User Requests", value: data?.stats.claimedRequests, icon: Users },
    ];

    const benefits = [
        "Manage your dealer page information",
        "Show verified ownership to customers",
        "Improve visibility and customer trust",
        "Receive better quality leads",
        "Request removal for incorrect listings",
        "Keep your business profile updated",
    ];

    return (
        <div className="font-poppins">
            <section className="relative overflow-hidden">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    className="relative w-full py-16"
                >
                    <div className="grid items-center gap-12 lg:grid-cols-2">
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <motion.div
                                    className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700"
                                >
                                    <ShieldCheck size={16} />
                                    Dealer Page Verification
                                </motion.div>

                                <ContextualHelpButton sectionId="page-claim" />

                                {data?.claimStatus === "Live" && (
                                    <motion.div
                                        className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-800"
                                    >
                                        <CheckCircle2 size={16} className="text-emerald-700" />
                                        Page is Claimed & Live
                                    </motion.div>
                                )}

                                {data?.claimStatus === "Pending" && (
                                    <motion.div
                                        className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800"
                                    >
                                        <ShieldCheck size={16} className="text-amber-600" />
                                        Claim Request Pending Approval
                                    </motion.div>
                                )}

                                {data?.claimStatus === "Removal Pending" && (
                                    <motion.div
                                        className="inline-flex items-center gap-2 rounded-full border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-800"
                                    >
                                        <Trash2 size={16} className="text-rose-600" />
                                        Removal Request Pending Approval
                                    </motion.div>
                                )}
                            </div>

                            <motion.h1
                                className="mt-6 max-w-2xl text-4xl font-bold tracking-tight text-slate-950 md:text-6xl"
                            >
                                Claim your dealer page with confidence
                            </motion.h1>

                            <motion.p
                                className="mt-5 max-w-xl text-base leading-8 text-slate-600 md:text-lg"
                            >
                                Take control of your business listing, update your details, build
                                customer trust, and request removal if this page should not be
                                listed.
                            </motion.p>

                            <motion.div
                                className="mt-8"
                            >

                                {/* BUTTONS */}
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    {/* LIVE: EDIT BUTTON */}
                                    {data?.claimStatus === "Live" ? (
                                        <Link
                                            to="/page-edit/current"
                                            style={{ backgroundColor: THEME }}
                                            className="group inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 cursor-pointer hover:-translate-y-0.5 hover:shadow-lg shadow-md shadow-[#B08D55]/25"
                                        >
                                            <Edit3 size={17} />
                                            Edit Your Page
                                            <ArrowRight
                                                size={17}
                                                className="transition-transform duration-300 group-hover:translate-x-1"
                                            />
                                        </Link>
                                    ) : (
                                        /* CLAIM BUTTON */
                                        <button
                                            onClick={() => setShowClaimModal(true)}
                                            disabled={!data?.isEligible || data?.claimStatus === "Pending"}
                                            style={{
                                                backgroundColor: data?.isEligible && data?.claimStatus !== "Pending" ? THEME : "#cbd5e1",
                                            }}
                                            className={`
                                                group inline-flex items-center justify-center gap-2
                                                rounded-xl px-6 py-3.5 text-sm font-semibold text-white
                                                transition-all duration-300
                                                ${data?.isEligible && data?.claimStatus !== "Pending"
                                                    ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg"
                                                    : "cursor-not-allowed opacity-80"
                                                }
                                            `}
                                        >
                                            {data?.claimStatus === "Pending"
                                                ? "Claim Pending Approval"
                                                : data?.claimStatus === "Removal Approved"
                                                ? "Reclaim your page"
                                                : "Claim Page"}

                                            {data?.isEligible && data?.claimStatus !== "Pending" ? (
                                                <ArrowRight
                                                    size={17}
                                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                                />
                                            ) : (
                                                <Lock size={16} />
                                            )}
                                        </button>
                                    )}

                                    {/* REMOVE BUTTON */}
                                    <button
                                        onClick={() => setShowRemoveModal(true)}
                                        disabled={!data?.isEligible || data?.claimStatus === "Removal Approved" || data?.claimStatus === "Removal Pending"}
                                        className={`
                                            group inline-flex items-center justify-center gap-2
                                            rounded-xl border px-6 py-3.5 text-sm font-semibold
                                            transition-all duration-300
                                            ${data?.isEligible && data?.claimStatus !== "Removal Approved" && data?.claimStatus !== "Removal Pending"
                                                ? "border-slate-200 bg-white text-slate-800 hover:-translate-y-0.5 hover:bg-slate-50 hover:shadow-md cursor-pointer"
                                                : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                            }
                                        `}
                                    >
                                        {data?.claimStatus === "Removal Pending" ? "Removal Pending" : "Request Removal"}

                                        {data?.isEligible && data?.claimStatus !== "Removal Approved" && data?.claimStatus !== "Removal Pending" ? (
                                            <Trash2
                                                size={17}
                                                className="transition-transform duration-300 group-hover:scale-110"
                                            />
                                        ) : (
                                            <Lock size={16} />
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                            {!data?.isEligible && (
                                <motion.p
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-4 text-sm text-slate-500"
                                >
                                    You need at least{" "}
                                    <span className="font-semibold">
                                        4 orders
                                    </span>{" "}
                                    to unlock claim and removal access.
                                </motion.p>
                            )}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 26 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.75,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="transform-gpu rounded-[2rem] border border-slate-200 bg-white p-4 shadow-xl shadow-slate-200/70 will-change-transform"
                        >
                            <div className="overflow-hidden rounded-[1.5rem]">
                                <img
                                    src="https://plus.unsplash.com/premium_photo-1661310036031-e0186692a717?q=80&w=869&auto=format&fit=crop"
                                    alt="Dealer business"
                                    className="h-72 w-full object-cover"
                                />
                            </div>

                            <div className="mt-5 grid grid-cols-2 gap-3">
                                {statCards.map((item, index) => {
                                    const Icon = item.icon;

                                    return (
                                        <motion.div
                                            key={item.label}
                                            initial={{ opacity: 0, y: 18 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{
                                                delay: 0.25 + index * 0.08,
                                                duration: 0.55,
                                                ease: [0.22, 1, 0.36, 1],
                                            }}
                                            className="transform-gpu rounded-2xl border border-slate-100 bg-slate-50 p-4 shadow-sm transition-colors duration-300 hover:bg-white hover:shadow-md will-change-transform"
                                        >
                                            <div style={{ color: THEME }} className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm">
                                                <Icon size={20} />
                                            </div>

                                            <h3 className="text-2xl font-bold text-slate-950">
                                                {item.value}
                                            </h3>

                                            <p className="mt-1 text-sm text-slate-500">
                                                {item.label}
                                            </p>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </section>

            <section className="w-full py-14">
                <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                    <motion.div
                        initial={{ opacity: 0, y: 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                            duration: 0.65,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="transform-gpu rounded-3xl border border-slate-200 bg-white p-7 shadow-sm will-change-transform"
                    >
                        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl \">
                            <BarChart3 style={{ color: THEME }} size={24} />
                        </div>

                        <h2 className="text-2xl font-bold text-slate-950">
                            Why should you claim this page?
                        </h2>

                        <p className="mt-4 leading-7 text-slate-600">
                            A claimed page helps customers know that your business information
                            is correct, trusted, and managed by the real owner.
                        </p>
                    </motion.div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        {benefits.map((item, index) => (
                            <motion.div
                                key={item}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-70px" }}
                                transition={{
                                    delay: index * 0.04,
                                    duration: 0.55,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="flex transform-gpu items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow duration-300 hover:shadow-md will-change-transform"
                            >
                                <CheckCircle2
                                    style={{ color: THEME }}
                                    size={20}
                                    className="mt-0.5 shrink-0 text-emerald-600"
                                />

                                <p className="text-sm font-medium leading-6 text-slate-700">
                                    {item}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <Modal open={showClaimModal}>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                        Claim Request
                    </h2>
                    <p className="mt-2 text-gray-600">
                        Are you sure you want to request for claim this page?
                    </p>
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={() => setShowClaimModal(false)}

                            className=" cursor-pointer rounded-xl bg-gray-100 px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleClaimPage}
                            disabled={loading}
                            className={`w-20  cursor-pointer flex justify-center rounded-xl px-5 py-3 text-sm font-semibold text-white disabled:opacity-50 bg-green-600 hover:bg-green-700
                                }`}
                        >
                            {loading ? <div className="h-5 w-5 border-2 border-zinc-300 border-t-2 border-r-white animate-spin rounded-full duration-1000"></div> : "Claim"}
                        </button>
                    </div>
                </div>
            </Modal>
            <Modal open={showRemoveModal}>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                        Removal Request
                    </h2>
                    <p className="mt-2 text-gray-600">
                        Are you sure you want to request for remove this page?
                    </p>
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={() => setShowRemoveModal(false)}

                            className="cursor-pointer rounded-xl bg-gray-100 px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleRemovePage}
                            disabled={loading}
                            className="w-24 cursor-pointer flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold text-white disabled:opacity-50 bg-red-600 hover:bg-red-700"
                        >
                            {loading ? <div className="h-5 w-5 border-2 border-zinc-300 border-t-2 border-r-white animate-spin rounded-full duration-1000"></div> : "Remove"}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ClaimPage;